import React from 'react';
import { Box, Container, Flex, Placeholder, Icon, Modal, Button, Text, Card, styled } from '@goodrx/matisse';

import pluralize from 'pluralize';
import { useToggledState } from 'app/hooks';
import { CdnImage } from 'app/components/CdnImage';
import { truncateText } from 'app/lib/utils';
import { Coupon as CouponType } from 'app/types/Coupon';
import { CouponNotFound } from 'app/components/coupons/CouponNotFound';
import { SendCouponCta } from 'app/components/coupons/SendCouponCta';
import { CouponDisclaimer } from 'app/components/coupons/CouponDisclaimer';

const CouponHeader = styled(Flex)`
  padding-bottom: 8px;
  border-bottom: 1px solid ${(p) => p.theme.colors.greyLight};
`;

const DrugName = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
`;

const DoseForm = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(p) => p.theme.colors.greyDarkest};
`;

const Pharmacy = styled(Flex)`
  padding-top: 12px;
  margin-top: 16px;
  border-top: 1px solid ${(p) => p.theme.colors.greyLight};
`;

const PharmacyName = styled.div`
  cursor: pointer;
  color: ${(p) => p.theme.colors.greyDark};
`;

const GoodRxLogo = styled(CdnImage)`
  width: 50px;
`;

const CouponEntry = ({ title, value }: any) => (
  <Flex justifyContent="space-between" mt={3}>
    <Text fontWeight={600}>{title}</Text>
    <Text fontWeight={500}>{value}</Text>
  </Flex>
);

interface CouponProps {
  coupon?: CouponType;
  loading: boolean;
  error?: string;
  visitPrescriptionId?: number;
  showDisclaimer?: boolean;
  styles?: {
    couponCard: React.CSSProperties;
  };
}
/*
 * Generates and displays a coupon for the provided drug and pharmacy using the
 * GoodRx APIs. Required params are:
 * - ndc: drug identifier
 * - ncpdp: pharmacy identifier
 */
export const Coupon: React.FC<CouponProps> = ({
  error,
  coupon,
  styles,
  loading,
  visitPrescriptionId,
  showDisclaimer,
  ...props
}) => {
  const [pharmacyModalOpen, togglePharmacyModal] = useToggledState(false);

  const { name: pharmacyName, addressLine1, addressLine2, state, city, zip } = coupon?.pharmacy || {};

  return (
    <Card style={styles?.couponCard} width={{ _: '100%', sm: 350 }} {...props}>
      <Card.Body>
        <Placeholder
          loading={loading}
          error={error}
          renderError={() => (
            <Container textAlign="center">
              <strong>Oh no!</strong>

              <Box mt={1}>{error}</Box>
            </Container>
          )}>
          {coupon ? (
            <>
              <CouponHeader justifyContent="space-between">
                <Flex flexDirection="column">
                  <DrugName>{truncateText(coupon.display)}</DrugName>
                  <DoseForm>
                    {pluralize(coupon.form!, coupon.quantity, true)} - {coupon.dosage}
                  </DoseForm>
                </Flex>

                <Flex justifyContent="start" flexDirection="column">
                  <Text color="secondaryDark">${Number(coupon.price).toFixed(2)}</Text>
                </Flex>
              </CouponHeader>

              <Flex justifyContent="space-between" flexDirection="column">
                <CouponEntry title="Member ID" value={coupon.memberId} />
                <CouponEntry title="Group" value={coupon.groupNumber} />
                <CouponEntry title="Bin" value={coupon.rxbin} />
                <CouponEntry title="PCN" value={coupon.rxpcn} />
              </Flex>

              <Pharmacy justifyContent="space-between" alignItems="center">
                <PharmacyName role="button" onClick={togglePharmacyModal}>
                  <Icon name="map-marker" color="primary" />
                  <Text color="primary" ml={2}>
                    {truncateText(pharmacyName, 20)}
                  </Text>
                </PharmacyName>
                <GoodRxLogo path="images/logos/goodrx.landscape.svg" />
              </Pharmacy>

              {visitPrescriptionId && <SendCouponCta mt={3} visitPrescriptionId={visitPrescriptionId} />}

              {showDisclaimer && (
                <Flex justifyContent="space-between" flexDirection="column">
                  <CouponDisclaimer coupon={coupon} />
                </Flex>
              )}

              <Modal open={pharmacyModalOpen} onClose={togglePharmacyModal} title="Pharmacy Address" minWidth={350}>
                <Modal.Body>
                  <Flex flexDirection="column">
                    <Text fontWeight={600} mb={2}>
                      {pharmacyName}
                    </Text>
                    <Text color="greyDark">{addressLine1}</Text>
                    <Text color="greyDark">{addressLine2}</Text>

                    <Text color="greyDark" mb={2}>
                      {city}, {state} {zip}
                    </Text>

                    <Button onClick={togglePharmacyModal} block outline>
                      Close
                    </Button>
                  </Flex>
                </Modal.Body>
              </Modal>
            </>
          ) : (
            <CouponNotFound />
          )}
        </Placeholder>
      </Card.Body>
    </Card>
  );
};
